/**
 * WHAT THE OVERLAY EDITOR DID TO THE HERO, IN WORDS THE INTAKE CAN CARRY (2026-10-09).
 *
 * The ✎ Edit Overlay editor (vendor/ovSky.js, the old form's code) bakes headline, sky, fade,
 * a seasonal overlay or Included badges into ONE flattened JPEG. Once baked, the image field
 * holds that JPEG — so `Main Image Info` reported a bare hosted URL and the person building the
 * email lost two things: the words on the photo (they could not copy them, and could not check
 * the spelling against the brief) and the Bynder photo underneath (no DAM link — the bake is not
 * a Bynder asset).
 *
 * Both are already on the block: the editor keeps every setting as `ov*` keys and the pristine
 * photo as `<key>__ovbase`. This file turns them into lines for the SAME column the hero already
 * uses — `Main Image Info` — rather than new columns: Katy's Capture reads one cell, and the
 * ECH contract is to ask her before adding any (Wiki/Infrastructure/ech-block-app-airtable-
 * contract.md, step 4). Same shapes as the GIF hero: "Label · value", never a leading "N. "
 * (richText renumbers it).
 */
type Data = Record<string, unknown>;
const T = (v: unknown): string => (v == null ? "" : String(v)).trim();

/** how many Included badges the editor placed (`ovBadges` is a JSON array of {x,y}) */
export function ovBadgeCount(v: unknown): number {
  try { const a = JSON.parse(String(v || "[]")); return Array.isArray(a) ? a.length : 0; } catch { return 0; }
}

/** the seasonal overlay the Effect tab picked, or "" */
export function ovEffect(d: Data, key: string): string {
  const fx = T(d[key + "__ovfx"]);
  return fx && fx !== "none" ? fx : "";
}

/** text is on the photo only when the editor's own switch says so AND there is copy (`__ovCommit`) */
export function ovTextOn(d: Data): boolean {
  return T(d.ovOn) === "1" && !!(T(d.ovHead) || T(d.ovKicker) || T(d.ovSub));
}

/** did the editor change the pixels at all — the same test `__ovCommit` uses to decide to bake */
export function ovActive(d: Data, key: string): boolean {
  return ovTextOn(d) || !!ovEffect(d, key) || +T(d.ovSky) > 0 || +T(d.ovFade) > 0 || ovBadgeCount(d.ovBadges) > 0;
}

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * The overlay half of `Main Image Info`, or null when the hero has no overlay.
 *   Overlay hero — <the hosted bake>
 *   On the photo · Eyebrow: … | Headline: … | Subtext: … | Button: …
 *   Look · White text, bottom left, headline 44px, scrim 40%, sky extension 30%, fade to white 20%, seasonal overlay: Fireworks, 2 Included badges
 *   Source photo — <what mainImageInfo says for the pristine photo>
 */
export function overlayInfoLines(d: Data, key: string, describePhoto: (url: string) => string): string | null {
  if (!ovActive(d, key)) return null;
  const baked = T(d[key]);
  const base = T(d[key + "__ovbase"]);
  /* settings alone are not an overlay: no stashed photo means nothing was baked, and a GIF hero
     replaced the bake (the overlay card is hidden on a GIF, but its keys can linger) */
  if (!base || base === baked || T(d[key + "__gif"]) === "1") return null;
  const out: string[] = [];
  out.push("Overlay hero — " + (/^data:/i.test(baked) ? "(not hosted yet)" : baked));
  if (ovTextOn(d)) {
    const words = [
      ["Eyebrow", T(d.ovKicker)], ["Headline", T(d.ovHead)], ["Subtext", T(d.ovSub)], ["Button", T(d.ovCta)],
    ].filter(([, v]) => v).map(([l, v]) => l + ": " + v);
    if (words.length) out.push("On the photo · " + words.join(" | "));
  }
  const look: string[] = [];
  if (ovTextOn(d)) {
    const col = T(d.ovCol).toLowerCase();
    look.push((col === "#ffffff" || col === "#fff" || !col ? "White" : col === "#1b2a58" ? "Navy" : T(d.ovCol)) + " text");
    look.push((T(d.ovV) || "bottom") + " " + (T(d.ovAlign) || "left"));
    if (T(d.ovSize)) look.push("headline " + T(d.ovSize) + "px");
    if (+T(d.ovScrim) > 0) look.push("scrim " + T(d.ovScrim) + "%");
  }
  if (+T(d.ovSky) > 0) look.push("sky extension " + T(d.ovSky) + "%");
  if (+T(d.ovFade) > 0) look.push("fade to white " + T(d.ovFade) + "%");
  const fx = ovEffect(d, key);
  if (fx) look.push("seasonal overlay: " + cap(fx));
  const nb = ovBadgeCount(d.ovBadges);
  if (nb) look.push(nb + " Included badge" + (nb === 1 ? "" : "s"));
  /* copy under the photo makes the hero taller than the Height dropdown (overlaySession `ovDrawH`) */
  if (/^\d+$/.test(T(d.ovDrawH))) look.push("drawn " + T(d.ovDrawH) + "px tall with the copy below the photo");
  if (look.length) out.push("Look · " + cap(look.join(", ")));
  if (base && base !== baked) out.push("Source photo — " + describePhoto(base));
  return out.join("\n");
}
